import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import { LocationState } from "history";
import { useDidUpdate } from "hooks";
import { useAllNoteSelect, useDealActions, useDealIdSelector, useShareDialogSelector } from "store";
import { INote } from "store/types";

import { INoteData, NoteEditor } from "./Note-editor/NoteEditor";
// import { Search } from "components/Search/Search";
import { NotesList } from "./Notes-list/NotesList";

import "./Notes.styles.scss";

type Settings = {
  mode: "create" | "update" | null;
  state?: LocationState;
};

export const Notes: React.FC = () => {
  const [mode, setMode] = useState<"create" | "update" | null>(null);
  const [editorLoading, setEditorLoading] = useState<boolean>(false);
  const [selectedNote, setSelectedNote] = useState<INote | null>(null);
  const isShareDialogOpen = !!useShareDialogSelector();

  const notes = useAllNoteSelect();
  const list = useMemo(() => {
    const sortedNotes = notes.sort((a, b) => b.id - a.id);

    if (!selectedNote || mode === "create") {
      return sortedNotes;
    } else {
      return sortedNotes.filter((item) => item.id !== selectedNote?.id);
    }
  }, [notes, selectedNote]);
  const dealId = useDealIdSelector();

  const { createNoteAction, updateNoteAction, getNotesAction } = useDealActions();

  useDidUpdate(() => {
    if (!dealId || isShareDialogOpen) return;
    getNotesAction(dealId);
  }, [isShareDialogOpen]);

  const bodyRef = useRef<HTMLDivElement>(null);

  const location = useLocation<Settings>();
  const params: Settings = location?.state ?? {};

  useEffect(() => {
    if ("mode" in params) {
      setMode(params?.mode);
    }
  }, [params]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo(0, 0);
    }
  }, [selectedNote]);

  const editorAction = useCallback(
    async (noteData: INoteData) => {
      if (noteData.value.length > 60000) {
        return;
      }
      if (!dealId) {
        return;
      }

      if (noteData.RTLmode) {
        // eslint-disable-next-line quotes
        noteData.value = noteData.value.replace(/<(\w+)>/g, '<$1 dir="rtl">');
      }
      try {
        setEditorLoading(true);
        if (mode === "create") {
          await createNoteAction({
            entityType: "deal",
            entityId: dealId,
            title: noteData.title,
            text: noteData.value,
            deal_id: dealId
          });
        } else {
          if (!selectedNote || (selectedNote.title === noteData.title && selectedNote.text === noteData.value)) {
            return;
          }
          if (noteData.title) {
            await updateNoteAction({
              id: selectedNote.id,
              title: noteData.title,
              text: noteData.value
            });
          } else {
            await updateNoteAction({
              id: selectedNote.id,
              text: noteData.value
            });
          }
        }
      } catch (error) {
        throw error;
      } finally {
        setEditorLoading(false);
        setMode(null);
        setSelectedNote(null);
      }
    },
    [mode, selectedNote, dealId]
  );

  return (
    <div className="notes">
      <div className="notes-head">
        <Button
          onClick={() => {
            setMode("create");
          }}
          variant="contained"
          startIcon={<AddIcon />}
        >
          Add Note
        </Button>
      </div>
      <div ref={bodyRef} className="notes-body">
        {mode && (
          <NoteEditor
            mode={mode}
            note={selectedNote}
            loading={editorLoading}
            onCancel={() => {
              setSelectedNote(null);
              setMode(null);
            }}
            onSave={editorAction}
          />
        )}

        <NotesList
          list={list}
          onClickEditNote={(note: INote) => {
            setSelectedNote(note);
            setMode("update");
          }}
        />
      </div>
    </div>
  );
};
